import type {
  InsuranceArticleCategory,
  InsuranceArticleContent,
  InsuranceProductConfig,
} from './insuranceArticleTypes';

export const AUTO_LAST_MODIFIED = 'June 2, 2026';
export const AUTO_BASE_PATH = '/knowledge-base/auto';

const autoHref = (slug: string) => `${AUTO_BASE_PATH}/${slug}`;

export const AUTO_ARTICLE_CATEGORIES: InsuranceArticleCategory[] = [
  {
    name: 'Fundamentals',
    articles: [
      {
        title: 'What is car insurance?',
        slug: 'what-is-car-insurance',
        href: autoHref('what-is-car-insurance'),
        description:
          'Learn what auto insurance covers, how policies work in Texas, and why liability and physical damage coverage matter.',
        keywords: ['what is car insurance', 'auto insurance basics', 'Texas car insurance', 'policy overview'],
        status: 'published',
      },
      {
        title: 'Texas minimum liability car insurance',
        slug: 'texas-minimum-liability-car-insurance',
        href: autoHref('texas-minimum-liability-car-insurance'),
        description:
          'Understand Texas 30/60/25 minimum liability limits, what they pay for, and why many drivers need more than the legal minimum.',
        keywords: ['Texas minimum car insurance', '30/60/25', 'liability limits', 'state requirements'],
        status: 'published',
      },
      {
        title: 'Full coverage vs. liability only',
        slug: 'full-coverage-vs-liability-only-car-insurance',
        href: autoHref('full-coverage-vs-liability-only-car-insurance'),
        description:
          'Compare liability-only and full-coverage auto insurance so you can choose the right protection for your car and budget.',
        keywords: ['full coverage vs liability', 'liability only', 'comprehensive collision', 'Texas auto'],
        status: 'published',
      },
      {
        title: 'How much car insurance do I need in Texas?',
        slug: 'how-much-car-insurance-do-i-need-in-texas',
        href: autoHref('how-much-car-insurance-do-i-need-in-texas'),
        description:
          'Practical guidance on liability limits, UM/UIM, deductibles, and when full coverage makes sense for Texas drivers.',
        keywords: ['how much car insurance', 'Texas coverage amounts', 'liability limits', 'recommended coverage'],
        status: 'published',
      },
      {
        title: 'What is SR-22 insurance in Texas?',
        slug: 'what-is-sr22-insurance-in-texas',
        href: autoHref('what-is-sr22-insurance-in-texas'),
        description:
          'Learn what an SR-22 is in Texas — proof of financial responsibility, who needs it, and how it differs from a separate insurance policy.',
        keywords: ['SR-22 Texas', 'financial responsibility filing', 'license reinstatement', 'high-risk driver'],
        status: 'published',
      },
      {
        title: 'What is gap insurance?',
        slug: 'what-is-gap-insurance',
        href: autoHref('what-is-gap-insurance'),
        description:
          'Gap insurance covers the difference between your loan balance and your car\'s value after a total loss — when you need it and how it works.',
        keywords: ['gap insurance', 'loan lease gap', 'total loss coverage', 'upside down car loan'],
        status: 'published',
      },
    ],
  },
  {
    name: 'Coverage Types',
    articles: [
      {
        title: 'What is comprehensive car insurance?',
        slug: 'what-is-comprehensive-car-insurance',
        href: autoHref('what-is-comprehensive-car-insurance'),
        description:
          'See what comprehensive coverage pays for — theft, hail, flood, animal strikes — and when Texas drivers should add it.',
        keywords: ['comprehensive car insurance', 'non-collision coverage', 'theft hail flood', 'Texas auto'],
        status: 'published',
      },
      {
        title: 'What is collision car insurance?',
        slug: 'what-is-collision-car-insurance',
        href: autoHref('what-is-collision-car-insurance'),
        description:
          'Learn how collision coverage works after accidents with other vehicles or objects, and how deductibles affect your claim.',
        keywords: ['collision coverage', 'accident damage', 'car repair insurance', 'deductible'],
        status: 'published',
      },
      {
        title: 'Uninsured & underinsured motorist coverage',
        slug: 'uninsured-underinsured-motorist-coverage',
        href: autoHref('uninsured-underinsured-motorist-coverage'),
        description:
          'Protect yourself when the at-fault driver has little or no insurance — especially important in Texas.',
        keywords: ['UM UIM coverage', 'uninsured motorist', 'underinsured motorist', 'Texas auto'],
        status: 'published',
      },
      {
        title: 'What is personal injury protection (PIP) in Texas?',
        slug: 'what-is-personal-injury-protection-pip-texas',
        href: autoHref('what-is-personal-injury-protection-pip-texas'),
        description:
          'PIP pays medical bills and lost wages for you and passengers after an accident, regardless of fault — optional in Texas.',
        keywords: ['PIP Texas', 'personal injury protection', 'no-fault medical auto', 'Texas auto coverage'],
        status: 'published',
      },
      {
        title: 'What is medical payments coverage (MedPay)?',
        slug: 'what-is-medical-payments-coverage-medpay',
        href: autoHref('what-is-medical-payments-coverage-medpay'),
        description:
          'MedPay covers accident-related medical expenses for you and passengers — how it differs from PIP and health insurance.',
        keywords: ['MedPay car insurance', 'medical payments coverage', 'auto medical bills', 'Texas MedPay'],
        status: 'published',
      },
      {
        title: 'Does car insurance cover rental cars?',
        slug: 'does-car-insurance-cover-rental-cars',
        href: autoHref('does-car-insurance-cover-rental-cars'),
        description:
          'When your Texas auto policy extends to rental cars, what rental reimbursement covers, and gaps to check before you travel.',
        keywords: ['rental car insurance', 'rental reimbursement', 'borrowed vehicle coverage', 'Texas auto'],
        status: 'published',
      },
      {
        title: 'Does car insurance cover hail damage in Texas?',
        slug: 'does-car-insurance-cover-hail-damage-texas',
        href: autoHref('does-car-insurance-cover-hail-damage-texas'),
        description:
          'Hail damage is usually covered under comprehensive insurance in Texas — filing a claim, deductibles, and total loss basics.',
        keywords: ['hail damage car insurance', 'Texas hail claim', 'comprehensive hail', 'storm damage auto'],
        status: 'published',
      },
    ],
  },
  {
    name: 'Cost & Savings',
    articles: [
      {
        title: 'What affects car insurance in Texas?',
        slug: 'how-much-does-car-insurance-cost-in-texas',
        href: autoHref('how-much-does-car-insurance-cost-in-texas'),
        description:
          'Understand the driver, vehicle, and coverage choices insurers weigh when building a Texas auto policy.',
        keywords: ['Texas auto insurance factors', 'car insurance rating', 'coverage choices', 'Texas auto policy'],
        status: 'published',
      },
      {
        title: 'How to lower your car insurance premium',
        slug: 'how-to-lower-car-insurance-premium',
        href: autoHref('how-to-lower-car-insurance-premium'),
        description:
          'Practical ways to reduce your Texas auto premium — discounts, deductibles, bundling, and safe driving habits.',
        keywords: ['lower car insurance', 'discounts', 'save on auto insurance', 'Texas premium tips'],
        status: 'published',
      },
      {
        title: 'Car insurance for teen drivers in Texas',
        slug: 'car-insurance-for-teen-drivers-in-texas',
        href: autoHref('car-insurance-for-teen-drivers-in-texas'),
        description:
          'Coverage and cost tips for insuring teen drivers in Texas, including GDL rules, discounts, and policy structure.',
        keywords: ['teen driver insurance Texas', 'young driver auto', 'GDL Texas', 'student discounts'],
        status: 'published',
      },
    ],
  },
  {
    name: 'Buying & Claims',
    articles: [
      {
        title: 'How do car insurance deductibles work?',
        slug: 'how-do-car-insurance-deductibles-work',
        href: autoHref('how-do-car-insurance-deductibles-work'),
        description:
          'Learn how deductibles apply to comprehensive and collision claims and how your choice affects premium and out-of-pocket cost.',
        keywords: ['car insurance deductible', 'comprehensive deductible', 'collision deductible', 'out of pocket'],
        status: 'published',
      },
      {
        title: 'Does car insurance follow the car or the driver?',
        slug: 'does-car-insurance-follow-the-car-or-driver',
        href: autoHref('does-car-insurance-follow-the-car-or-driver'),
        description:
          'Understand when coverage follows the vehicle vs. the named driver — and what happens when someone else drives your car.',
        keywords: ['car vs driver insurance', 'permissive use', 'borrowed car coverage', 'Texas auto policy'],
        status: 'published',
      },
      {
        title: 'How to file a car insurance claim',
        slug: 'how-to-file-a-car-insurance-claim',
        href: autoHref('how-to-file-a-car-insurance-claim'),
        description:
          'Step-by-step checklist for reporting an auto claim in Texas — from the scene to repair and settlement.',
        keywords: ['file car insurance claim', 'auto claim process', 'accident reporting', 'Texas claims'],
        status: 'published',
      },
      {
        title: 'How to switch car insurance companies',
        slug: 'how-to-switch-car-insurance-companies',
        href: autoHref('how-to-switch-car-insurance-companies'),
        description:
          'Switch auto carriers in Texas without a coverage gap — timing, cancellation, and what to compare before you move.',
        keywords: ['switch car insurance', 'change auto carrier', 'cancel policy', 'Texas insurance'],
        status: 'published',
      },
      {
        title: 'What happens if you drive without insurance in Texas?',
        slug: 'what-happens-if-you-drive-without-insurance-in-texas',
        href: autoHref('what-happens-if-you-drive-without-insurance-in-texas'),
        description:
          'Penalties, fines, license suspension, and financial risk of driving uninsured in Texas — and how to get legal again.',
        keywords: ['driving without insurance Texas', 'uninsured penalty', 'SR-22 Texas', 'no insurance fine'],
        status: 'published',
      },
      {
        title: 'Car insurance for rideshare drivers (Uber & Lyft)',
        slug: 'car-insurance-for-rideshare-drivers-uber-lyft',
        href: autoHref('car-insurance-for-rideshare-drivers-uber-lyft'),
        description:
          'Personal auto policies often exclude rideshare — understand Period 1–3 coverage, platform insurance, and Texas endorsements.',
        keywords: ['rideshare insurance Texas', 'Uber Lyft insurance', 'TNC coverage', 'gig driver auto'],
        status: 'published',
      },
      {
        title: 'Does car insurance cover windshield replacement?',
        slug: 'does-car-insurance-cover-windshield-replacement',
        href: autoHref('does-car-insurance-cover-windshield-replacement'),
        description:
          'When comprehensive or glass coverage pays for windshield repair or replacement in Texas — and how deductibles apply.',
        keywords: ['windshield replacement insurance', 'glass coverage Texas', 'comprehensive glass', 'cracked windshield claim'],
        status: 'published',
      },
      {
        title: 'What is non-owner car insurance?',
        slug: 'what-is-non-owner-car-insurance',
        href: autoHref('what-is-non-owner-car-insurance'),
        description:
          'Non-owner policies provide liability (and sometimes UM/UIM) when you drive borrowed or rented cars but do not own a vehicle.',
        keywords: ['non-owner car insurance', 'borrowed car liability', 'SR-22 non-owner', 'Texas auto'],
        status: 'published',
      },
      {
        title: 'Car insurance when financing or leasing a vehicle',
        slug: 'car-insurance-when-financing-or-leasing-a-vehicle',
        href: autoHref('car-insurance-when-financing-or-leasing-a-vehicle'),
        description:
          'Lender and lessor insurance requirements for financed and leased cars — physical damage, gap, and loss payee basics.',
        keywords: ['financed car insurance', 'leased vehicle coverage', 'lender requirements', 'loss payee Texas'],
        status: 'published',
      },
    ],
  },
];

export const AUTO_ARTICLE_CONTENT: Record<string, InsuranceArticleContent> = {
  'what-is-car-insurance': {
    slug: 'what-is-car-insurance',
    title: 'What is car insurance?',
    subtitle:
      'A plain-language overview of auto insurance in Texas — what policies cover, who they protect, and how the main coverages fit together. Ready to shop? See [[what affects car insurance in Texas|/knowledge-base/auto/how-much-does-car-insurance-cost-in-texas]].',
    metaTitle: 'What Is Car Insurance? Texas Guide | GetSkimp',
    metaDescription:
      'Learn what car insurance is, what liability and physical damage coverages do, and how auto policies work for Texas drivers.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Car insurance is a contract between you and an insurer: you pay a premium, and the company pays covered losses up to your policy limits after a deductible (if one applies). In Texas, you must carry at least minimum liability insurance to drive legally. Most drivers also add optional coverages to protect their own vehicle and themselves after accidents with uninsured drivers.',
        ],
      },
      {
        title: 'Main types of auto coverage',
        table: {
          headers: ['Coverage', 'What it typically pays for'],
          rows: [
            ['Bodily injury liability', 'Other people\'s medical bills when you are at fault'],
            ['Property damage liability', 'Damage you cause to someone else\'s car or property'],
            ['Comprehensive', 'Theft, hail, flood, fire, vandalism, animal hits (your car)'],
            ['Collision', 'Damage to your car from a crash, regardless of fault (subject to deductible)'],
            ['Uninsured/underinsured motorist (UM/UIM)', 'Your injuries or damage when the other driver lacks adequate insurance'],
            ['Medical payments (MedPay) / PIP', 'Medical expenses for you and passengers (varies by policy)'],
          ],
        },
      },
      {
        title: 'Why Texas drivers need more than the minimum',
        bullets: [
          'Texas law requires liability only — not coverage for your own car repairs. See [[Texas minimum liability requirements|/knowledge-base/auto/texas-minimum-liability-car-insurance]].',
          'A serious injury claim can exceed minimum limits quickly; many drivers choose 100/300 or higher liability. Read [[how much car insurance you need in Texas|/knowledge-base/auto/how-much-car-insurance-do-i-need-in-texas]].',
          'If you finance or lease a vehicle, your lender usually requires comprehensive and collision — often called [[full coverage vs. liability only|/knowledge-base/auto/full-coverage-vs-liability-only-car-insurance]].',
        ],
      },
      {
        title: 'How premiums are priced in Texas',
        paragraphs: [
          'Insurers look at your driving record, age, location (Houston, DFW, Austin, and rural Texas rate differently), vehicle type, annual miles, credit-based insurance scores (where allowed), and coverage limits. Comparing quotes with the same limits and deductibles is the fairest way to shop.',
        ],
        bullets: [
          'Higher liability limits cost more but reduce out-of-pocket risk after a bad accident.',
          'Raising your [[deductible|/knowledge-base/auto/how-do-car-insurance-deductibles-work]] usually lowers premium on comprehensive and collision.',
          'Bundling with renters or home insurance and maintaining continuous coverage often helps rates.',
        ],
      },
    ],
    ctaHeading: 'Compare Texas auto insurance with GetSkimp',
    ctaBody: 'Answer a few questions and see car insurance options built for Texas drivers.',
  },

  'how-much-does-car-insurance-cost-in-texas': {
    slug: 'how-much-does-car-insurance-cost-in-texas',
    title: 'What affects car insurance in Texas?',
    subtitle:
      'Insurers look at your coverage choices, driving history, vehicle, and location when they build a Texas auto policy. Pair this with [[how to lower your car insurance premium|/knowledge-base/auto/how-to-lower-car-insurance-premium]] when you are ready to shop.',
    metaTitle: 'What Affects Car Insurance in Texas? | GetSkimp',
    metaDescription:
      'Learn what Texas insurers consider when rating auto policies — coverage levels, driving history, vehicle type, location, and more.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Texas auto insurance is not one-size-fits-all. Carriers evaluate your chosen limits and deductibles, driving record, age and experience, where you garage the vehicle, what you drive, and how you use it. Comparing policies with the same limits and deductibles is the fairest way to evaluate options side by side.',
        ],
      },
      {
        title: 'Coverage choices that shape your policy',
        bullets: [
          '[[Texas minimum liability|/knowledge-base/auto/texas-minimum-liability-car-insurance]] vs. higher bodily injury and property damage limits.',
          '[[Full coverage|/knowledge-base/auto/full-coverage-vs-liability-only-car-insurance]] (comprehensive and collision) vs. liability only.',
          'Optional add-ons such as [[UM/UIM coverage|/knowledge-base/auto/uninsured-underinsured-motorist-coverage]], rental reimbursement, and roadside assistance.',
          'Deductible levels on comprehensive and collision — see [[how deductibles work|/knowledge-base/auto/how-do-car-insurance-deductibles-work]].',
        ],
      },
      {
        title: 'Driver and vehicle factors',
        bullets: [
          'Driving history: accidents, tickets, and prior claims.',
          'Age and experience — teen and newly licensed drivers are often rated differently.',
          'Vehicle type: repair complexity, safety features, theft rates, and whether you still owe on a loan.',
          'Annual mileage and primary use (commute, business, or pleasure).',
          'Credit-based insurance score: used by many Texas carriers (with regulatory limits).',
        ],
      },
      {
        title: 'Location and weather',
        bullets: [
          'Urban areas such as Houston and DFW tend to see more collision and theft claims than rural counties.',
          'Hail-prone regions may warrant strong comprehensive coverage for storm damage — see [[hail damage coverage|/knowledge-base/auto/does-car-insurance-cover-hail-damage-texas]].',
          'Zip code and garaging address affect how carriers classify your territory.',
        ],
      },
      {
        title: 'How to compare policies fairly',
        numbered: [
          'Match liability limits, deductibles, and optional coverages across quotes.',
          'Confirm whether the quote includes the same drivers and vehicles.',
          'Review [[how much coverage you need|/knowledge-base/auto/how-much-car-insurance-do-i-need-in-texas]] before you choose limits.',
          'Ask about discounts: multi-policy, good student, telematics, and defensive driving.',
          'Re-shop at renewal and when life changes (marriage, new car, moving).',
        ],
      },
    ],
    ctaHeading: 'Start auto insurance with GetSkimp',
    ctaBody: 'Answer a few questions and see car insurance options built for Texas drivers.',
  },

  'texas-minimum-liability-car-insurance': {
    slug: 'texas-minimum-liability-car-insurance',
    title: 'Texas minimum liability car insurance',
    subtitle:
      'Texas requires 30/60/25 liability limits for most drivers. Here is what those numbers mean and why many Texans buy more. Start with [[what car insurance is|/knowledge-base/auto/what-is-car-insurance]] if you are new to auto policies.',
    metaTitle: 'Texas Minimum Liability Car Insurance (30/60/25) | GetSkimp',
    metaDescription:
      'Understand Texas minimum auto liability limits (30/60/25), what they cover, penalties for driving uninsured, and when to buy higher limits.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'To drive legally in Texas, you must carry liability insurance with at least $30,000 bodily injury per person, $60,000 bodily injury per accident, and $25,000 property damage per accident — written as 30/60/25. This pays for injuries and property damage you cause to others; it does not pay to fix your own car. Driving without proof of insurance can lead to fines, fees, and suspension — see [[what happens if you drive without insurance|/knowledge-base/auto/what-happens-if-you-drive-without-insurance-in-texas]].',
        ],
      },
      {
        title: 'What 30/60/25 pays for',
        table: {
          headers: ['Limit', 'Meaning', 'Example'],
          rows: [
            ['$30,000 per person', 'Max BI payment to one injured person you hurt', 'One passenger\'s hospital bills up to $30k'],
            ['$60,000 per accident', 'Max BI for all people hurt in one accident you cause', 'Two injured parties share the $60k pool'],
            ['$25,000 property damage', 'Max for others\' cars or property you damage', 'Repairs to the other driver\'s vehicle'],
          ],
        },
      },
      {
        title: 'Why minimum limits may not be enough',
        bullets: [
          'One emergency room visit or surgery can exceed $30,000 per person quickly.',
          'New trucks and SUVs often cost more than $25,000 to replace — you pay the difference out of pocket.',
          'You can be sued for amounts above your limits; wages and assets may be at risk.',
          'Lenders require physical damage coverage on financed cars — minimum liability alone will not satisfy the loan.',
        ],
      },
      {
        title: 'Common higher limits Texas drivers choose',
        bullets: [
          '50/100/50 or 100/300/100 for better protection on bodily injury and property damage.',
          'Matching [[UM/UIM limits|/knowledge-base/auto/uninsured-underinsured-motorist-coverage]] to your liability limits.',
          'Use our guide on [[how much car insurance you need in Texas|/knowledge-base/auto/how-much-car-insurance-do-i-need-in-texas]] to pick a target.',
        ],
      },
    ],
  },

  'full-coverage-vs-liability-only-car-insurance': {
    slug: 'full-coverage-vs-liability-only-car-insurance',
    title: 'Full coverage vs. liability only',
    subtitle:
      '"Full coverage" usually means liability plus comprehensive and collision — not unlimited protection. Compare both approaches and see [[what comprehensive covers|/knowledge-base/auto/what-is-comprehensive-car-insurance]] and [[what collision covers|/knowledge-base/auto/what-is-collision-car-insurance]].',
    metaTitle: 'Full Coverage vs. Liability Only Car Insurance | GetSkimp',
    metaDescription:
      'Compare liability-only and full-coverage auto insurance in Texas, including cost, lender requirements, and when each makes sense.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Liability-only insurance pays for damage and injuries you cause to others — it meets Texas legal requirements but does not pay to repair your car. Full coverage (in common usage) adds comprehensive and collision so your vehicle is protected against theft, weather, and crashes, minus your deductible. Financed and leased cars almost always require comprehensive and collision; older paid-off cars may be fine with liability only if you can afford to replace the vehicle yourself.',
        ],
      },
      {
        title: 'Side-by-side comparison',
        table: {
          headers: ['', 'Liability only', 'Full coverage (liability + comp + collision)'],
          rows: [
            ['Legal to drive in Texas', 'Yes, if limits meet 30/60/25', 'Yes'],
            ['Pays for your car after hail or theft', 'No', 'Yes (comprehensive, minus deductible)'],
            ['Pays for your car after you hit a pole', 'No', 'Yes (collision, minus deductible)'],
            ['Typical premium', 'Lower', 'Higher'],
            ['Lender requirement', 'Usually not enough if you have a loan', 'Usually required'],
          ],
        },
      },
      {
        title: 'When liability only can make sense',
        bullets: [
          'Your car\'s market value is low and you could replace it without insurance payout.',
          'You have emergency savings to cover a total loss.',
          'You do not have a loan or lease requiring physical damage coverage.',
        ],
      },
      {
        title: 'When full coverage is worth it',
        bullets: [
          'You depend on your vehicle daily and could not easily absorb a large repair bill.',
          'You live in hail, flood, or high-theft areas common in parts of Texas.',
          'You want gap protection considerations if you owe more than the car is worth (ask your agent about loan/lease gap endorsement).',
          'Pair with [[how much coverage you need|/knowledge-base/auto/how-much-car-insurance-do-i-need-in-texas]] and [[what affects car insurance in Texas|/knowledge-base/auto/how-much-does-car-insurance-cost-in-texas]].',
        ],
      },
    ],
  },

  'what-is-comprehensive-car-insurance': {
    slug: 'what-is-comprehensive-car-insurance',
    title: 'What is comprehensive car insurance?',
    subtitle:
      'Comprehensive (often called "comp") covers your vehicle for non-collision losses — essential in hail-prone Texas. See how it pairs with [[collision coverage|/knowledge-base/auto/what-is-collision-car-insurance]] in a [[full coverage|/knowledge-base/auto/full-coverage-vs-liability-only-car-insurance]] policy.',
    metaTitle: 'What Is Comprehensive Car Insurance? | GetSkimp',
    metaDescription:
      'Learn what comprehensive auto insurance covers in Texas — theft, hail, flood, fire, vandalism — and how deductibles work.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Comprehensive coverage pays to repair or replace your car after covered events that are not collisions with another vehicle — theft, hail, flood, fire, falling objects, vandalism, and animal strikes are common examples. You choose a deductible (often $500 or $1,000); the insurer pays the rest up to your car\'s actual cash value. It is optional unless your lender requires it, but it is a core part of most "full coverage" policies in Texas.',
        ],
      },
      {
        title: 'Usually covered vs. usually excluded',
        table: {
          headers: ['Often covered', 'Often excluded or limited'],
          rows: [
            ['Theft of vehicle or parts', 'Wear and tear, mechanical breakdown'],
            ['Hail and windstorm (major Texas peril)', 'Intentional damage by you'],
            ['Flood and hurricane damage (check policy)', 'Personal items stolen from car (may need homeowners/renters)'],
            ['Fire', 'Racing or commercial use without endorsement'],
            ['Hitting a deer or other animal', 'Custom equipment unless scheduled'],
          ],
        },
      },
      {
        title: 'Texas-specific considerations',
        bullets: [
          'North Texas and Panhandle hail seasons drive many comprehensive claims — higher deductibles can reduce premium but increase your hail out-of-pocket.',
          'Coastal and Gulf areas: confirm flood and hurricane wording; some drivers add separate flood coverage where available.',
          'Glass damage may have a separate $0 or lower glass deductible on some policies — ask when quoting.',
          'Your [[deductible choice|/knowledge-base/auto/how-do-car-insurance-deductibles-work]] directly affects comprehensive premium.',
        ],
      },
    ],
  },

  'what-is-collision-car-insurance': {
    slug: 'what-is-collision-car-insurance',
    title: 'What is collision car insurance?',
    subtitle:
      'Collision pays to fix your car after a crash — whether you hit another car, a guardrail, or a pothole. Works with [[comprehensive coverage|/knowledge-base/auto/what-is-comprehensive-car-insurance]] for full physical damage protection.',
    metaTitle: 'What Is Collision Car Insurance? | GetSkimp',
    metaDescription:
      'Understand collision auto insurance in Texas — what it covers, how deductibles work, and when you need it on your policy.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Collision coverage pays to repair or replace your vehicle after it collides with another car or object, or rolls over — regardless of who was at fault, subject to your policy terms. You pay your collision deductible first; the insurer pays the remaining repair cost or actual cash value if the car is totaled. Texas does not require collision, but auto lenders almost always do.',
        ],
      },
      {
        title: 'Collision vs. comprehensive',
        table: {
          headers: ['Scenario', 'Collision', 'Comprehensive'],
          rows: [
            ['Rear-ended at a stoplight (your car damaged)', 'Often yes', 'No'],
            ['You slide on ice and hit a fence', 'Often yes', 'No'],
            ['Hail dents on hood', 'No', 'Often yes'],
            ['Car stolen', 'No', 'Often yes'],
            ['Hit a deer', 'No', 'Often yes (animal strike)'],
          ],
        },
      },
      {
        title: 'Fault, deductibles, and other insurance',
        bullets: [
          'If the other driver is at fault, their liability should pay — but you may use your collision and let your insurer subrogate (recover) from the other party.',
          'Higher [[deductibles|/knowledge-base/auto/how-do-car-insurance-deductibles-work]] lower premium but mean more out-of-pocket per claim.',
          'If the other driver is uninsured, [[UM/UIM property damage|/knowledge-base/auto/uninsured-underinsured-motorist-coverage]] may apply where you selected it.',
          'See [[how to file a claim|/knowledge-base/auto/how-to-file-a-car-insurance-claim]] after an accident.',
        ],
      },
    ],
  },

  'how-do-car-insurance-deductibles-work': {
    slug: 'how-do-car-insurance-deductibles-work',
    title: 'How do car insurance deductibles work?',
    subtitle:
      'Deductibles apply to comprehensive and collision — not usually to liability. Learn how to choose amounts that balance premium and risk, especially on [[full coverage policies|/knowledge-base/auto/full-coverage-vs-liability-only-car-insurance]].',
    metaTitle: 'How Do Car Insurance Deductibles Work? | GetSkimp',
    metaDescription:
      'Learn how auto insurance deductibles work on comprehensive and collision claims in Texas and how to choose the right amount.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'A deductible is the amount you pay out of pocket before your insurer pays on a comprehensive or collision claim. If repairs cost $4,000 and your deductible is $500, the insurer typically pays $3,500 (minus depreciation if applicable). Liability coverage does not use a deductible in the usual sense — limits are the cap instead. Common Texas deductibles are $500 or $1,000 for comp and collision; you can often choose them separately.',
        ],
      },
      {
        title: 'Example: claim payout math',
        table: {
          headers: ['Repair estimate', 'Deductible', 'Insurer pays (simplified)'],
          rows: [
            ['$3,200', '$500', '$2,700'],
            ['$8,000', '$1,000', '$7,000'],
            ['$12,000 (totaled, ACV $11,000)', '$1,000', '$10,000'],
          ],
        },
      },
      {
        title: 'How to choose a deductible',
        bullets: [
          'Pick an amount you could pay tomorrow without financial hardship — hail season in Texas can mean sudden claims.',
          'Higher deductible → lower monthly premium; lower deductible → higher premium, less shock at claim time.',
          'If your car is older and low value, a high deductible on collision may make sense — or drop collision entirely.',
          'Combine with [[how to lower your premium|/knowledge-base/auto/how-to-lower-car-insurance-premium]] and [[what affects Texas auto insurance|/knowledge-base/auto/how-much-does-car-insurance-cost-in-texas]].',
        ],
      },
      {
        title: 'Deductibles vs. liability limits',
        paragraphs: [
          'Do not confuse deductibles with liability limits. Your [[Texas minimum 30/60/25|/knowledge-base/auto/texas-minimum-liability-car-insurance]] limits are the maximum the insurer pays to others — not your out-of-pocket per claim. Underinsured accidents are why [[UM/UIM|/knowledge-base/auto/uninsured-underinsured-motorist-coverage]] matters.',
        ],
      },
    ],
  },

  'how-much-car-insurance-do-i-need-in-texas': {
    slug: 'how-much-car-insurance-do-i-need-in-texas',
    title: 'How much car insurance do I need in Texas?',
    subtitle:
      'Legal minimums are a floor, not a recommendation. Use this framework for liability, UM/UIM, and physical damage based on your assets and vehicle. Review [[Texas minimum liability|/knowledge-base/auto/texas-minimum-liability-car-insurance]] first.',
    metaTitle: 'How Much Car Insurance Do I Need in Texas? | GetSkimp',
    metaDescription:
      'Find out how much car insurance you need in Texas — liability limits, UM/UIM, comprehensive, collision, and asset protection tips.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'At minimum, buy Texas-required 30/60/25 liability. Most financial advisors and agents recommend higher liability — often 100/300/100 or more if you have income, home equity, or savings to protect. Add uninsured/underinsured motorist coverage at matching limits. Add comprehensive and collision if you cannot afford to replace your car or your lender requires it. Adjust deductibles to match your emergency fund.',
        ],
      },
      {
        title: 'Suggested coverage by situation',
        table: {
          headers: ['Situation', 'Liability', 'UM/UIM', 'Comp / collision'],
          rows: [
            ['New driver, tight budget, older car', '30/60/25 minimum legal', 'Strongly consider matching UM/UIM', 'Optional if car value is very low'],
            ['Homeowner with assets', '100/300/100 or higher', 'Match liability limits', 'Yes if car has meaningful value'],
            ['Financed or leased vehicle', 'Often 100/300 required by lender', 'Recommended', 'Required by lender'],
            ['High net worth / high risk', '250/500+ and umbrella discuss with agent', 'High limits', 'Yes + gap if upside-down on loan'],
          ],
        },
      },
      {
        title: 'Questions to ask yourself',
        bullets: [
          'If I caused a multi-injury accident, could my savings cover bills above my limits?',
          'What percent of Texas drivers are uninsured? (High — UM/UIM is valuable; see [[UM/UIM guide|/knowledge-base/auto/uninsured-underinsured-motorist-coverage]].)',
          'Could I replace my car tomorrow without a claim check?',
          'Am I required to carry [[full coverage|/knowledge-base/auto/full-coverage-vs-liability-only-car-insurance]]?',
        ],
      },
      {
        title: 'Cost vs. protection',
        paragraphs: [
          'Higher limits cost more but scale better than minimum coverage after a serious crash. Compare two or three limit tiers — see [[what affects car insurance in Texas|/knowledge-base/auto/how-much-does-car-insurance-cost-in-texas]] — before deciding.',
        ],
      },
    ],
  },

  'what-happens-if-you-drive-without-insurance-in-texas': {
    slug: 'what-happens-if-you-drive-without-insurance-in-texas',
    title: 'What happens if you drive without insurance in Texas?',
    subtitle:
      'Driving uninsured in Texas triggers fines, possible suspension, and personal liability for any damage you cause. Compare with [[Texas minimum liability requirements|/knowledge-base/auto/texas-minimum-liability-car-insurance]] and [[how to switch carriers|/knowledge-base/auto/how-to-switch-car-insurance-companies]] to get legal coverage.',
    metaTitle: 'Driving Without Insurance in Texas: Penalties | GetSkimp',
    metaDescription:
      'Learn Texas penalties for driving without car insurance — fines, impound fees, license suspension, SR-22, and financial risk after an accident.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Driving without valid auto liability insurance in Texas is illegal. If you are stopped or in an accident without coverage, you can face fines, court costs, vehicle impound fees, license and registration suspension, and an SR-22 requirement to reinstate. You also pay out of pocket for injuries and property damage you cause — which can reach hundreds of thousands of dollars.',
        ],
      },
      {
        title: 'Common consequences (overview)',
        table: {
          headers: ['Issue', 'What can happen'],
          rows: [
            ['Traffic stop / proof of insurance', 'Ticket, fines, court fees'],
            ['Accident while uninsured', 'Personal liability for all damages; possible lawsuit'],
            ['Repeat offenses', 'Higher fines, longer suspension, SR-22 filing'],
            ['Registration renewal', 'Texas verifies insurance electronically — lapse can block renewal'],
          ],
        },
        paragraphs: [
          'Penalties change with law and circumstance; courts and DPS enforce specific outcomes. This table is educational, not legal advice.',
        ],
      },
      {
        title: 'Financial risk beyond fines',
        bullets: [
          'You can be sued for medical bills, lost wages, and property damage with no insurer defending you.',
          'Wage garnishment and liens are possible after judgments.',
          'If you are hit by someone else while uninsured, recovering damage to your car is harder without your own coverage.',
        ],
      },
      {
        title: 'How to get legal again',
        numbered: [
          'Buy a policy meeting at least [[30/60/25 liability|/knowledge-base/auto/texas-minimum-liability-car-insurance]].',
          'File SR-22 if ordered — your insurer submits proof of financial responsibility.',
          'Pay reinstatement fees to Texas DPS and resolve tickets.',
          'Maintain continuous coverage; lapses restart problems and raise future premiums.',
        ],
      },
    ],
  },

  'how-to-lower-car-insurance-premium': {
    slug: 'how-to-lower-car-insurance-premium',
    title: 'How to lower your car insurance premium',
    subtitle:
      'Practical ways to cut your Texas auto premium without dropping essential protection. Start with [[what affects car insurance in Texas|/knowledge-base/auto/how-much-does-car-insurance-cost-in-texas]] to understand rating factors.',
    metaTitle: 'How to Lower Car Insurance Premium in Texas | GetSkimp',
    metaDescription:
      'Tips to lower your Texas car insurance premium — discounts, deductibles, bundling, credit, and shopping strategies without sacrificing needed coverage.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Lower premiums by combining discounts, adjusting comprehensive/collision deductibles, maintaining a clean driving record, bundling policies, and comparing quotes from multiple carriers at renewal. Avoid cutting liability or UM/UIM to unsafe levels just to save money — one serious accident can cost far more than years of premium.',
        ],
      },
      {
        title: 'Discounts to ask about',
        table: {
          headers: ['Discount type', 'Typical requirement'],
          rows: [
            ['Multi-policy bundle', 'Auto + renters or home with same carrier'],
            ['Good driver / claim-free', 'No accidents or violations for 3–5 years'],
            ['Good student', 'Young driver with B average or better (varies)'],
            ['Telematics / safe driving app', 'Voluntary driving data monitoring'],
            ['Defensive driving course', 'Texas-approved course completion'],
            ['Military, alumni, employer', 'Affinity group programs'],
          ],
        },
      },
      {
        title: 'Structural changes that reduce premium',
        bullets: [
          'Raise [[comprehensive and collision deductibles|/knowledge-base/auto/how-do-car-insurance-deductibles-work]] if you have savings.',
          'Drop collision on very old, low-value cars — keep adequate liability and UM/UIM.',
          'Review garaging address accuracy; moving from urban to suburban can change rates.',
          'Improve credit over time — many Texas insurers use insurance scores.',
        ],
      },
      {
        title: 'Shopping smarter',
        numbered: [
          'Compare identical limits and deductibles across at least three quotes.',
          'Time [[switching companies|/knowledge-base/auto/how-to-switch-car-insurance-companies]] so there is no coverage gap.',
          'Ask about pay-in-full vs. monthly installment fees.',
          'Re-quote after life events: marriage, new job, paid-off loan, teen off policy.',
        ],
      },
    ],
  },

  'car-insurance-for-teen-drivers-in-texas': {
    slug: 'car-insurance-for-teen-drivers-in-texas',
    title: 'Car insurance for teen drivers in Texas',
    subtitle:
      'Insuring a teen in Texas is expensive but manageable with the right policy structure, discounts, and Graduated Driver License rules. See also [[how to lower your premium|/knowledge-base/auto/how-to-lower-car-insurance-premium]].',
    metaTitle: 'Car Insurance for Teen Drivers in Texas | GetSkimp',
    metaDescription:
      'Texas teen driver insurance guide — GDL rules, adding to a parent policy, discounts, and how to control premium for young drivers.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Teen drivers in Texas pay among the highest auto premiums because inexperience increases claim frequency. Adding a teen to a parent\'s policy is usually cheaper than a standalone policy. Enforce Texas Graduated Driver License (GDL) restrictions, maintain good grades for discounts, and choose higher deductibles on physical damage only if you can afford them. Never skimp on liability or UM/UIM limits.',
        ],
      },
      {
        title: 'Texas GDL phases (summary)',
        table: {
          headers: ['Phase', 'Age (typical)', 'Key restrictions'],
          rows: [
            ['Learner license', '15+', 'Licensed adult 21+ in front seat; no handheld phone'],
            ['Provisional license', '16–17', 'Night driving limits; passenger limits'],
            ['Full license', '18+', 'Standard adult driving rules'],
          ],
        },
        paragraphs: [
          'Verify current Texas DPS requirements — rules and ages can be updated by statute.',
        ],
      },
      {
        title: 'Ways families save',
        bullets: [
          'Add the teen to the household policy instead of a separate policy when possible.',
          'Good student discount — often requires 3.0 GPA or equivalent; ask your carrier.',
          'Assign the teen to the least expensive car to insure (not always the car they drive most — confirm permissive use rules in [[car vs. driver|/knowledge-base/auto/does-car-insurance-follow-the-car-or-driver]]).',
          'Telematics programs that reward safe braking and speed compliance.',
          'Driver education and defensive driving courses approved in Texas.',
        ],
      },
      {
        title: 'Coverage recommendations for teens',
        bullets: [
          'Keep liability at least at family standard — often well above [[state minimum|/knowledge-base/auto/texas-minimum-liability-car-insurance]].',
          'Strong [[UM/UIM|/knowledge-base/auto/uninsured-underinsured-motorist-coverage]] — teens have a higher crash risk.',
          'Comprehensive and collision on the vehicle they primarily use if it has value.',
        ],
      },
    ],
  },

  'does-car-insurance-follow-the-car-or-driver': {
    slug: 'does-car-insurance-follow-the-car-or-driver',
    title: 'Does car insurance follow the car or the driver?',
    subtitle:
      'In Texas, auto insurance generally follows the car — with important exceptions for permissive drivers, exclusions, and non-owned vehicles. Related: [[how to file a claim|/knowledge-base/auto/how-to-file-a-car-insurance-claim]].',
    metaTitle: 'Does Car Insurance Follow the Car or Driver? | GetSkimp',
    metaDescription:
      'Learn whether Texas car insurance follows the car or the driver, permissive use, exclusions, and rental or borrowed vehicle coverage.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Texas auto policies usually follow the insured vehicle first. If a friend borrows your car with permission and causes an accident, your policy is typically primary for liability and physical damage (subject to policy terms). If you drive someone else\'s car occasionally, their insurance usually pays first; your policy may be secondary for liability in some cases. Named driver exclusions, business use, and unlisted household drivers are common gaps — read your declarations page.',
        ],
      },
      {
        title: 'Typical scenarios',
        table: {
          headers: ['Scenario', 'Primary coverage (usual)'],
          rows: [
            ['Friend borrows your car with permission', 'Your auto policy'],
            ['You borrow a friend\'s car', 'Friend\'s policy first; your policy may supplement'],
            ['Rental car', 'Rental contract + your policy or credit card benefits — verify before trip'],
            ['Delivery or rideshare without endorsement', 'Often excluded — need commercial or rideshare add-on'],
            ['Excluded driver listed on policy', 'No coverage if that person drives'],
          ],
        },
      },
      {
        title: 'What to verify on your policy',
        bullets: [
          'Permissive use language and any age restrictions for occasional drivers.',
          'All licensed household members listed — undisclosed drivers can void claims.',
          'Business use, food delivery, and rideshare require endorsements or commercial coverage.',
          'Non-owner policies exist if you rent or borrow frequently but do not own a car.',
        ],
      },
    ],
  },

  'how-to-file-a-car-insurance-claim': {
    slug: 'how-to-file-a-car-insurance-claim',
    title: 'How to file a car insurance claim',
    subtitle:
      'A practical Texas auto claims checklist — safety first, documentation, and working with your adjuster. Know your [[deductible|/knowledge-base/auto/how-do-car-insurance-deductibles-work]] before you call.',
    metaTitle: 'How to File a Car Insurance Claim in Texas | GetSkimp',
    metaDescription:
      'Step-by-step guide to filing a car insurance claim in Texas — at the scene, reporting, documentation, repairs, and settlement.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'After an accident: ensure safety, call 911 if there are injuries, exchange information, photograph damage and the scene, and report the claim to your insurer promptly — many policies require timely notice. Do not admit fault at the scene. Your insurer assigns an adjuster, reviews coverage (liability, collision, comprehensive, or UM/UIM), and guides repair or total loss settlement minus your deductible where applicable.',
        ],
      },
      {
        title: 'At the scene',
        numbered: [
          'Move to safety if possible; use hazards and cones on Texas highways.',
          'Call police for injuries, major damage, or disputed fault — get report number.',
          'Exchange names, phones, license plates, insurance cards, and driver licenses.',
          'Photograph all vehicles, plates, street signs, skid marks, and injuries from a distance.',
          'Get witness names and phone numbers.',
          'Avoid discussing fault or signing anything except police or medical forms.',
        ],
      },
      {
        title: 'When reporting to your insurer',
        bullets: [
          'Policy number, date/time/location, description of what happened.',
          'Other driver\'s insurance if applicable; police report number.',
          'Photos and contact info you collected.',
          'Rental car coverage if you have loss of use / rental reimbursement on your policy.',
        ],
      },
      {
        title: 'Repair vs. total loss',
        paragraphs: [
          'If repairs exceed a threshold of actual cash value, the insurer may total the vehicle and pay ACV minus deductible. You can negotiate total loss valuation with comparable sales data. For at-fault accidents with an uninsured other driver, see [[UM/UIM coverage|/knowledge-base/auto/uninsured-underinsured-motorist-coverage]].',
        ],
      },
      {
        title: 'If you were uninsured',
        paragraphs: [
          'You may face personal liability and DPS penalties — read [[driving without insurance in Texas|/knowledge-base/auto/what-happens-if-you-drive-without-insurance-in-texas]]. Buy coverage before driving again.',
        ],
      },
    ],
  },

  'uninsured-underinsured-motorist-coverage': {
    slug: 'uninsured-underinsured-motorist-coverage',
    title: 'Uninsured & underinsured motorist coverage',
    subtitle:
      'UM/UIM protects you when the at-fault driver has no insurance or not enough. Especially valuable in Texas. Pair with [[how much coverage you need|/knowledge-base/auto/how-much-car-insurance-do-i-need-in-texas]].',
    metaTitle: 'Uninsured & Underinsured Motorist Coverage Texas | GetSkimp',
    metaDescription:
      'Learn uninsured and underinsured motorist (UM/UIM) coverage in Texas — what it pays, limits, stacking, and why many drivers buy it.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Uninsured motorist (UM) coverage helps when an at-fault driver has no insurance or flees the scene (hit-and-run, subject to policy proof rules). Underinsured motorist (UIM) helps when the at-fault driver\'s limits are too low to cover your injuries or damage. Texas insurers must offer UM/UIM — you can reject it in writing, but many agents recommend limits matching your liability. Texas has a high rate of uninsured drivers compared to many states.',
        ],
      },
      {
        title: 'UM vs. UIM (simplified)',
        table: {
          headers: ['Coverage', 'Triggers when', 'Often pays for'],
          rows: [
            ['Uninsured motorist bodily injury (UMBI)', 'At-fault driver has no insurance', 'Your medical bills, pain and suffering (per policy)'],
            ['Uninsured motorist property damage (UMPD)', 'Uninsured driver damages your car', 'Vehicle repair (Texas UMPD rules vary — confirm)'],
            ['Underinsured motorist (UIM)', 'At-fault limits exhausted', 'Remaining injury costs above other driver\'s limits'],
          ],
        },
      },
      {
        title: 'Why Texas drivers buy UM/UIM',
        bullets: [
          'Estimated uninsured driver rate is well above the national average in many studies.',
          'Minimum [[30/60/25 liability|/knowledge-base/auto/texas-minimum-liability-car-insurance]] may not cover your hospital bills.',
          'Hit-and-run claims may fall under UM with proper documentation.',
          'Relatively low premium increase vs. liability for the protection gained.',
        ],
      },
      {
        title: 'Shopping tips',
        bullets: [
          'Match UM/UIM bodily injury limits to your liability BI limits when possible.',
          'Understand whether UMPD has a deductible and how it interacts with [[collision|/knowledge-base/auto/what-is-collision-car-insurance]].',
          'Rejecting UM/UIM must be documented — do not reject without understanding the risk.',
        ],
      },
    ],
  },

  'how-to-switch-car-insurance-companies': {
    slug: 'how-to-switch-car-insurance-companies',
    title: 'How to switch car insurance companies',
    subtitle:
      'Change Texas auto carriers without a coverage gap or surprise fees. Review [[what affects car insurance in Texas|/knowledge-base/auto/how-much-does-car-insurance-cost-in-texas]] before you cancel.',
    metaTitle: 'How to Switch Car Insurance Companies in Texas | GetSkimp',
    metaDescription:
      'How to switch car insurance in Texas without a lapse — when to cancel, start new coverage, SR-22, and what to compare on quotes.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Start your new Texas auto policy before canceling the old one — even a one-day lapse can trigger higher rates and DPS issues. Pick the same effective date for new start and old cancellation. Compare limits, deductibles, UM/UIM, and endorsements apples-to-apples. After the new policy is active, cancel the prior policy in writing and keep proof of insurance in your vehicle.',
        ],
      },
      {
        title: 'Step-by-step switch checklist',
        numbered: [
          'Quote new policies with identical [[coverage levels|/knowledge-base/auto/how-much-car-insurance-do-i-need-in-texas]] and [[deductibles|/knowledge-base/auto/how-do-car-insurance-deductibles-work]].',
          'Bind the new policy with a start date — usually midnight local time on the chosen day.',
          'Cancel the old policy effective the same datetime — avoid overlap only if the carrier refunds pro-rata; a few hours overlap is safer than a gap.',
          'Download ID cards and confirm Texas SR-22 transfer if you have one.',
          'Notify your lienholder of the new lender loss payee information if you have a loan.',
        ],
      },
      {
        title: 'Watch for these pitfalls',
        bullets: [
          'Cancellation fees on short-term policies — ask your old carrier.',
          'Automatic payments continuing after cancel — turn off autopay.',
          'Missing rental, roadside, or gap coverage you had before.',
          'Undisclosed drivers or garaging changes that void the new policy.',
        ],
      },
      {
        title: 'After you switch',
        paragraphs: [
          'Texas verifies insurance through its system — ensure your VIN and policy report correctly. If you were previously uninsured, see [[penalties for no insurance|/knowledge-base/auto/what-happens-if-you-drive-without-insurance-in-texas]] and reinstate your license properly.',
        ],
      },
    ],
  },

  'what-is-sr22-insurance-in-texas': {
    slug: 'what-is-sr22-insurance-in-texas',
    title: 'What is SR-22 insurance in Texas?',
    subtitle:
      'An SR-22 is not a separate insurance policy — it is a certificate your insurer files with Texas proving you carry required liability coverage. Often needed after [[driving without insurance|/knowledge-base/auto/what-happens-if-you-drive-without-insurance-in-texas]] or serious violations.',
    metaTitle: 'What Is SR-22 Insurance in Texas? | GetSkimp',
    metaDescription:
      'Learn what SR-22 means in Texas, who must file it, how long it lasts, and how the certificate ties to your auto liability policy and license reinstatement.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'SR-22 is a Certificate of Financial Responsibility — a form your auto insurer files with the Texas Department of Public Safety (DPS) confirming you have at least the state-required liability insurance. You still buy a normal auto policy; the SR-22 is an administrative filing attached to it. Courts or DPS typically require it after certain convictions (DUI, driving without insurance, repeated tickets) or to reinstate a suspended license. You must keep continuous coverage for the ordered period — usually two years — or the filing lapses and your license can be suspended again.',
        ],
      },
      {
        title: 'SR-22 vs. regular car insurance',
        table: {
          headers: ['', 'Regular auto policy', 'SR-22 filing'],
          rows: [
            ['What it is', 'Insurance contract with coverage limits', 'Proof document filed by your insurer'],
            ['Covers accidents', 'Yes, per policy terms', 'No — it only proves you have coverage'],
            ['Required for all drivers', 'Yes, to drive legally in Texas', 'Only for drivers ordered by court or DPS'],
            ['Meets Texas 30/60/25', 'If limits are adequate', 'Filing confirms adequate liability is in force'],
          ],
        },
      },
      {
        title: 'Common reasons Texas requires an SR-22',
        bullets: [
          'Driving without insurance or failing to show proof at a stop.',
          'DWI/DUI or other serious traffic convictions.',
          'Too many points on your driving record within a period.',
          'At-fault accident while uninsured.',
          'License suspension reinstatement after administrative actions.',
        ],
      },
      {
        title: 'How to get and maintain an SR-22',
        numbered: [
          'Buy or renew a policy meeting at least [[Texas minimum liability 30/60/25|/knowledge-base/auto/texas-minimum-liability-car-insurance]].',
          'Ask your insurer to file the SR-22 with Texas DPS — there is usually a one-time filing fee.',
          'Keep coverage continuous for the full requirement period; any lapse can reset the clock or suspend your license.',
          'If you do not own a car, a [[non-owner policy|/knowledge-base/auto/what-is-non-owner-car-insurance]] with SR-22 may satisfy the requirement.',
        ],
      },
      {
        title: 'What happens when the SR-22 period ends',
        paragraphs: [
          'After the ordered period with no lapses, the filing requirement typically ends. Your insurer may still classify you as a higher-risk driver for underwriting purposes, but you no longer need the certificate on file with DPS. Shop carriers at renewal — see [[how to switch car insurance companies|/knowledge-base/auto/how-to-switch-car-insurance-companies]] — and maintain at least minimum liability to stay legal.',
        ],
      },
    ],
    ctaHeading: 'Compare Texas auto insurance with GetSkimp',
    ctaBody: 'Answer a few questions and see car insurance options built for Texas drivers.',
  },

  'what-is-gap-insurance': {
    slug: 'what-is-gap-insurance',
    title: 'What is gap insurance?',
    subtitle:
      'Gap (Guaranteed Asset Protection) covers the shortfall when your totaled car\'s value is less than what you still owe on the loan or lease. Especially relevant when [[financing or leasing a vehicle|/knowledge-base/auto/car-insurance-when-financing-or-leasing-a-vehicle]].',
    metaTitle: 'What Is Gap Insurance? Texas Guide | GetSkimp',
    metaDescription:
      'Gap insurance explained for Texas drivers — what it covers after a total loss, loan vs. lease, and how it works with comprehensive and collision on your policy.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Gap insurance pays the difference between your auto insurer\'s total-loss settlement (actual cash value) and the remaining balance on your auto loan or lease — minus any deductible or exclusions on the gap policy. It does not replace [[comprehensive or collision|/knowledge-base/auto/full-coverage-vs-liability-only-car-insurance]]; your physical damage coverage must first declare the vehicle a total loss. Gap is most useful when you put little money down, have a long loan term, or drive a car that depreciates faster than you pay down principal.',
        ],
      },
      {
        title: 'Example: total loss without gap',
        table: {
          headers: ['Item', 'Amount (example)'],
          rows: [
            ['Loan balance remaining', '$22,000'],
            ['Insurer actual cash value payout', '$18,000'],
            ['Collision deductible you pay', '$1,000'],
            ['Out-of-pocket "gap" without gap insurance', '$5,000'],
            ['With gap insurance (if eligible)', 'Gap may pay the $5,000 shortfall'],
          ],
        },
        paragraphs: [
          'Numbers are illustrative. Gap policies have terms, exclusions, and maximum benefit limits — read the contract.',
        ],
      },
      {
        title: 'Where to buy gap coverage',
        bullets: [
          'Your auto insurer as a loan/lease gap endorsement on the policy — often the simplest at claim time.',
          'The dealership or lender at purchase — may be a separate finance product; compare terms before signing.',
          'Some credit unions and banks bundle gap with the loan paperwork.',
          'Leased vehicles: gap or similar waiver is sometimes included in the lease — check your contract before buying duplicate coverage.',
        ],
      },
      {
        title: 'What gap usually does not cover',
        bullets: [
          'Missed loan payments, late fees, or negative equity rolled from a prior trade-in beyond policy limits.',
          'Extended warranties or aftermarket equipment not covered by your auto policy.',
          'Repairs that are not a total loss — only total loss scenarios trigger gap.',
          'Vehicles used commercially or for rideshare without proper coverage — see [[rideshare insurance|/knowledge-base/auto/car-insurance-for-rideshare-drivers-uber-lyft]].',
        ],
      },
      {
        title: 'Gap vs. new car replacement',
        paragraphs: [
          'Some carriers offer "new car replacement" or similar endorsements instead of traditional gap — they may pay for a new vehicle of the same make/model after a total loss within the first year or two of ownership. Ask your agent which product fits your loan structure and compare with [[how deductibles work|/knowledge-base/auto/how-do-car-insurance-deductibles-work]] on your comp and collision.',
        ],
      },
    ],
    ctaHeading: 'Compare Texas auto insurance with GetSkimp',
    ctaBody: 'Answer a few questions and see car insurance options built for Texas drivers.',
  },

  'what-is-personal-injury-protection-pip-texas': {
    slug: 'what-is-personal-injury-protection-pip-texas',
    title: 'What is personal injury protection (PIP) in Texas?',
    subtitle:
      'PIP helps pay medical bills and lost wages after an auto accident, regardless of who was at fault. Texas is not a no-fault state, but PIP is offered on most policies. Compare with [[MedPay|/knowledge-base/auto/what-is-medical-payments-coverage-medpay]] and your health insurance.',
    metaTitle: 'Personal Injury Protection (PIP) in Texas | GetSkimp',
    metaDescription:
      'Texas PIP auto insurance explained — medical bills, lost wages, funeral benefits, and how personal injury protection differs from MedPay and liability.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Personal Injury Protection (PIP) is optional first-party coverage on Texas auto policies. It pays reasonable medical expenses, a portion of lost income, and sometimes funeral costs for you and covered passengers after a motor vehicle accident — without requiring you to prove the other driver was at fault. Texas law requires insurers to offer PIP; you can reject it in writing. Default offer is often $2,500 per person unless you select higher limits or reject coverage.',
        ],
      },
      {
        title: 'What PIP typically covers',
        table: {
          headers: ['Benefit', 'Common use'],
          rows: [
            ['Medical expenses', 'Hospital, doctor, ambulance, rehab after a covered accident'],
            ['Lost wages', 'Partial income replacement while you cannot work (limits apply)'],
            ['Essential services', 'Household help you cannot perform due to injury (if included)'],
            ['Funeral expenses', 'Burial costs within policy limits (if included)'],
          ],
        },
      },
      {
        title: 'PIP vs. MedPay vs. liability',
        bullets: [
          '[[Liability insurance|/knowledge-base/auto/texas-minimum-liability-car-insurance]] pays others when you are at fault — not your own medical bills.',
          '[[MedPay|/knowledge-base/auto/what-is-medical-payments-coverage-medpay]] pays medical costs only; no wage replacement in most policies.',
          'PIP includes medical plus wage and other benefits depending on the endorsement — broader than MedPay in many cases.',
          '[[UM/UIM|/knowledge-base/auto/uninsured-underinsured-motorist-coverage]] applies when another driver is at fault and lacks adequate insurance — different trigger than PIP.',
        ],
      },
      {
        title: 'Should you keep or reject PIP?',
        bullets: [
          'If you have strong health insurance with low out-of-pocket costs, you might rely on health first — but PIP can cover deductibles and copays health will not.',
          'If you lack health insurance, PIP is often valuable for quick accident-related medical payment.',
          'Rejecting PIP must be documented — do not sign a rejection without understanding you lose no-fault-style benefits on your auto policy.',
          'Higher PIP limits (e.g., $10,000 or $25,000) are available from many Texas carriers for a modest increase in premium.',
        ],
      },
      {
        title: 'Coordination with health insurance',
        paragraphs: [
          'Texas policies may coordinate PIP with health insurance — meaning health pays first and PIP fills gaps, or PIP pays first depending on policy language. Ask your agent how subrogation works so you are not surprised at claim time. After any accident, follow [[how to file a car insurance claim|/knowledge-base/auto/how-to-file-a-car-insurance-claim]] and keep all medical bills and records.',
        ],
      },
    ],
    ctaHeading: 'Compare Texas auto insurance with GetSkimp',
    ctaBody: 'Answer a few questions and see car insurance options built for Texas drivers.',
  },

  'what-is-medical-payments-coverage-medpay': {
    slug: 'what-is-medical-payments-coverage-medpay',
    title: 'What is medical payments coverage (MedPay)?',
    subtitle:
      'MedPay pays accident-related medical bills for you and your passengers — a simple add-on to Texas auto policies. See how it compares to [[PIP in Texas|/knowledge-base/auto/what-is-personal-injury-protection-pip-texas]] and when it complements health insurance.',
    metaTitle: 'Medical Payments Coverage (MedPay) Explained | GetSkimp',
    metaDescription:
      'MedPay car insurance covers accident medical bills for you and passengers in Texas — limits, claims, and how medical payments differ from PIP and health plans.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Medical payments coverage (MedPay) pays reasonable and necessary medical expenses resulting from a motor vehicle accident for you and passengers in your insured vehicle — regardless of fault, up to your selected limit. Common limits are $1,000, $2,500, $5,000, or $10,000 per person. MedPay is optional in Texas. It does not pay lost wages or pain and suffering; it is narrowly focused on medical bills, which makes it simpler than [[PIP|/knowledge-base/auto/what-is-personal-injury-protection-pip-texas]] for many drivers.',
        ],
      },
      {
        title: 'MedPay vs. PIP (simplified)',
        table: {
          headers: ['', 'MedPay', 'PIP (Texas)'],
          rows: [
            ['Primary benefit', 'Medical expenses', 'Medical + often lost wages and other benefits'],
            ['Fault required', 'No', 'No'],
            ['Optional in Texas', 'Yes', 'Yes (must be offered; can reject PIP)'],
            ['Typical limits', '$1k – $10k per person', '$2,500 default offer; higher available'],
          ],
        },
      },
      {
        title: 'When MedPay is useful',
        bullets: [
          'You have a high-deductible health plan and want auto coverage for emergency room copays after a crash.',
          'Passengers in your car lack health insurance — MedPay can cover their treatment up to limits.',
          'You want a small, predictable medical benefit without buying full PIP.',
          'Pedestrian or cyclist injuries you cause may also be covered under some MedPay forms — confirm with your policy.',
        ],
      },
      {
        title: 'How MedPay works with other coverage',
        bullets: [
          'Health insurance often pays first; MedPay may cover deductibles, copays, or amounts health denies — policy language controls order of payment.',
          'At-fault driver\'s liability should pay your bills in theory — but [[uninsured drivers|/knowledge-base/auto/uninsured-underinsured-motorist-coverage]] are common in Texas, so first-party MedPay or PIP speeds treatment.',
          'MedPay is not a substitute for [[adequate liability limits|/knowledge-base/auto/how-much-car-insurance-do-i-need-in-texas]] if you injure others.',
        ],
      },
      {
        title: 'Filing a MedPay claim',
        numbered: [
          'Seek medical treatment and keep itemized bills and diagnosis codes.',
          'Notify your auto insurer promptly — same timeliness rules as other claims; see [[filing a claim|/knowledge-base/auto/how-to-file-a-car-insurance-claim]].',
          'Submit bills to MedPay adjuster; they pay up to limit per person.',
          'Understand whether MedPay has a right to be reimbursed if you later recover from the at-fault party (subrogation).',
        ],
      },
    ],
    ctaHeading: 'Compare Texas auto insurance with GetSkimp',
    ctaBody: 'Answer a few questions and see car insurance options built for Texas drivers.',
  },

  'does-car-insurance-cover-rental-cars': {
    slug: 'does-car-insurance-cover-rental-cars',
    title: 'Does car insurance cover rental cars?',
    subtitle:
      'Your Texas auto policy may extend to rental vehicles — but coverage is not automatic for every situation. Distinguish between renting on vacation and [[rental reimbursement after a claim|/knowledge-base/auto/how-to-file-a-car-insurance-claim]].',
    metaTitle: 'Does Car Insurance Cover Rental Cars? | GetSkimp',
    metaDescription:
      'Find out when your Texas auto policy covers rental cars, what rental reimbursement pays, credit card coverage, and gaps before you decline the counter waiver.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'If you carry comprehensive and collision on your personal auto policy in Texas, those coverages often extend to a rental car you are driving temporarily — subject to your policy\'s "replacement transportation" or "non-owned auto" language, limits, and exclusions. Liability from your policy usually follows you into the rental as well. However, the rental company may still bill you for loss of use, diminished value, or administrative fees your policy does not cover. Rental reimbursement on your policy is different: it pays for a rental while your own car is in the shop after a covered claim, up to daily and total limits.',
        ],
      },
      {
        title: 'Two different "rental" situations',
        table: {
          headers: ['Situation', 'What typically applies'],
          rows: [
            ['You rent a car on a trip', 'Your policy liability, comp, and collision may extend; check declarations'],
            ['Your car is in the shop after a claim', 'Rental reimbursement endorsement on your policy (if purchased)'],
            ['Someone else\'s car you borrow', 'Owner\'s policy first; yours may be secondary — see [[car vs. driver|/knowledge-base/auto/does-car-insurance-follow-the-car-or-driver]]'],
            ['Rideshare or delivery in a rental', 'Often excluded — personal policy and rental contract may deny coverage'],
          ],
        },
      },
      {
        title: 'What your policy may not cover on rentals',
        bullets: [
          'Loss of use charges while the rental company\'s car is repaired.',
          'Diminished value or administrative fees billed by the rental agency.',
          'Renting in Mexico or overseas — many Texas policies are U.S.-only unless endorsed.',
          'Renting a larger vehicle class than your policy covers — confirm class limits.',
        ],
      },
      {
        title: 'Credit card and rental counter coverage',
        bullets: [
          'Some credit cards provide secondary collision damage waiver if you pay with that card and decline the agency\'s waiver — read the card benefit guide.',
          'Rental counter LDW/CDW waives the agency\'s physical damage claim against you but is not liability insurance — you may still need liability from your auto policy.',
          'If you have no car and rent frequently, consider [[non-owner car insurance|/knowledge-base/auto/what-is-non-owner-car-insurance]].',
        ],
      },
      {
        title: 'Before you drive off the lot',
        numbered: [
          'Call your insurer or read your policy summary for extension to non-owned autos.',
          'Match rental liability limits to your personal [[liability limits|/knowledge-base/auto/how-much-car-insurance-do-i-need-in-texas]].',
          'Document pre-existing damage on the rental with photos.',
          'If your car is in the shop, confirm rental reimbursement daily limits and days maximum on your endorsement.',
        ],
      },
    ],
    ctaHeading: 'Compare Texas auto insurance with GetSkimp',
    ctaBody: 'Answer a few questions and see car insurance options built for Texas drivers.',
  },

  'does-car-insurance-cover-hail-damage-texas': {
    slug: 'does-car-insurance-cover-hail-damage-texas',
    title: 'Does car insurance cover hail damage in Texas?',
    subtitle:
      'Hail damage is one of the most common comprehensive claims in Texas. Coverage requires [[comprehensive insurance|/knowledge-base/auto/what-is-comprehensive-car-insurance]] — liability alone will not pay for dents on your car.',
    metaTitle: 'Does Car Insurance Cover Hail Damage in Texas? | GetSkimp',
    metaDescription:
      'Hail damage car insurance in Texas — comprehensive coverage, deductibles, repair vs. total loss, and steps to take after a North Texas or Panhandle hailstorm.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Yes — hail damage to your vehicle is covered under comprehensive (other-than-collision) coverage on a standard Texas auto policy, minus your comprehensive deductible. Texas liability-only policies do not pay for hail dents, broken glass, or paint damage on your own car. After a major hail event in Dallas–Fort Worth, the Panhandle, or Central Texas, repair shops and insurers see heavy claim volume — file promptly and document damage.',
        ],
      },
      {
        title: 'Covered vs. not covered (hail-related)',
        table: {
          headers: ['Scenario', 'Usually covered?', 'Coverage type'],
          rows: [
            ['Dents on hood, roof, trunk from hail', 'Yes, if you have comp', 'Comprehensive'],
            ['Windshield cracked by hail', 'Often yes — some policies have separate glass deductible', 'Comprehensive / glass'],
            ['Hail while car is parked at home', 'Yes', 'Comprehensive'],
            ['You only have liability insurance', 'No repair payment for your car', 'N/A'],
            ['Cosmetic-only waiver by insurer', 'Policy-specific — ask adjuster', 'Varies'],
          ],
        },
      },
      {
        title: 'Filing a hail claim in Texas',
        numbered: [
          'Photograph all panels, glass, and interior water intrusion if seals failed.',
          'Report to your insurer — catastrophic storms may have dedicated hotlines.',
          'Choose a reputable body shop; insurers may have preferred networks but you can often pick your shop.',
          'Pay your [[comprehensive deductible|/knowledge-base/auto/how-do-car-insurance-deductibles-work]] unless you have a zero-deductible glass endorsement.',
          'If repair cost approaches actual cash value, the car may be totaled — see [[gap insurance|/knowledge-base/auto/what-is-gap-insurance]] if you owe on a loan.',
        ],
      },
      {
        title: 'Texas regions with frequent hail claims',
        bullets: [
          'DFW metro and North Texas — repeated spring hailstorms drive comprehensive losses.',
          'Amarillo, Lubbock, and Panhandle — large hail stones and high claim frequency.',
          'San Antonio and Austin corridors — growing suburban exposure as development expands.',
          'Many drivers choose higher comprehensive deductibles to manage premiums — weigh that against [[hail season out-of-pocket|/knowledge-base/auto/how-do-car-insurance-deductibles-work]].',
        ],
      },
      {
        title: 'Will a hail claim raise my rates?',
        paragraphs: [
          'Comprehensive claims are often treated differently than at-fault collision claims by insurers, but not every company guarantees no impact. Multiple weather claims in a few years can still affect renewal. Keeping [[full coverage|/knowledge-base/auto/full-coverage-vs-liability-only-car-insurance]] on vehicles you cannot afford to replace out of pocket remains the practical choice in hail-prone Texas counties.',
        ],
      },
    ],
    ctaHeading: 'Compare Texas auto insurance with GetSkimp',
    ctaBody: 'Answer a few questions and see car insurance options built for Texas drivers.',
  },

  'car-insurance-for-rideshare-drivers-uber-lyft': {
    slug: 'car-insurance-for-rideshare-drivers-uber-lyft',
    title: 'Car insurance for rideshare drivers (Uber & Lyft)',
    subtitle:
      'Standard Texas personal auto policies often exclude transporting passengers for a fee. Understand Period 1–3, platform-provided insurance, and rideshare endorsements before you drive for [[Uber or Lyft|/knowledge-base/auto/does-car-insurance-follow-the-car-or-driver]].',
    metaTitle: 'Rideshare Car Insurance (Uber & Lyft) Texas | GetSkimp',
    metaDescription:
      'Texas rideshare insurance for Uber and Lyft drivers — Period 1–3 coverage gaps, TNC endorsements, platform insurance, and personal auto policy exclusions.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'If you drive for Uber, Lyft, or another transportation network company (TNC) in Texas, your personal auto policy likely excludes commercial rideshare use unless you add a rideshare endorsement or buy a commercial policy. The TNC provides limited liability coverage during certain app-on periods, but gaps exist — especially Period 1 (app on, waiting for a ride request) when only contingent coverage may apply. Texas drivers should notify their insurer, add proper coverage, and understand how platform insurance layers with their own policy.',
        ],
      },
      {
        title: 'Rideshare "periods" explained',
        table: {
          headers: ['Period', 'When', 'Coverage notes (typical)'],
          rows: [
            ['Period 1', 'App on, no passenger, no accepted trip', 'Largest gap — personal policy often denies; TNC may offer contingent liability only'],
            ['Period 2', 'Trip accepted, en route to pickup', 'TNC policy usually provides higher liability limits'],
            ['Period 3', 'Passenger in vehicle', 'TNC policy at highest limits; your personal policy still often excluded'],
            ['App off', 'Personal driving', 'Your standard Texas auto policy applies'],
          ],
        },
      },
      {
        title: 'What to buy in Texas',
        bullets: [
          'Rideshare endorsement on a personal policy — extends personal coverage into Period 1 and fills gaps (carrier availability varies).',
          'Commercial auto or TNC-specific policy if you drive full-time or your insurer does not offer endorsements.',
          'Maintain [[Texas minimum liability|/knowledge-base/auto/texas-minimum-liability-car-insurance]] or higher on your base policy — platforms do not replace your legal obligation to insure the vehicle.',
          'Physical damage: [[comprehensive and collision|/knowledge-base/auto/full-coverage-vs-liability-only-car-insurance]] may still exclude Period 2–3 unless endorsed — confirm with your agent.',
        ],
      },
      {
        title: 'Platform insurance is not complete',
        bullets: [
          'Uber and Lyft publish insurance summaries by period — read the current document for Texas; limits change over time.',
          'Deductibles on platform collision coverage for your car can be high — you may owe thousands before their policy pays.',
          'You are responsible for passengers\' injuries if coverage disputes arise — adequate [[UM/UIM|/knowledge-base/auto/uninsured-underinsured-motorist-coverage]] on your personal policy still matters when not in a TNC period.',
        ],
      },
      {
        title: 'Other gig driving',
        paragraphs: [
          'Food delivery (DoorDash, Uber Eats) and package delivery often need separate endorsements or commercial coverage — "business use" exclusions on personal policies are similar to rideshare. Do not assume [[car vs. driver rules|/knowledge-base/auto/does-car-insurance-follow-the-car-or-driver]] protect you while the app is on. Disclose all app-based driving to your agent before a claim is denied.',
        ],
      },
    ],
    ctaHeading: 'Compare Texas auto insurance with GetSkimp',
    ctaBody: 'Answer a few questions and see car insurance options built for Texas drivers.',
  },

  'does-car-insurance-cover-windshield-replacement': {
    slug: 'does-car-insurance-cover-windshield-replacement',
    title: 'Does car insurance cover windshield replacement?',
    subtitle:
      'Windshield damage from road debris, hail, or accidents may be covered under [[comprehensive insurance|/knowledge-base/auto/what-is-comprehensive-car-insurance]] — sometimes with a $0 glass deductible in Texas.',
    metaTitle: 'Does Car Insurance Cover Windshield Replacement? | GetSkimp',
    metaDescription:
      'Windshield replacement and repair coverage on Texas auto policies — comprehensive, glass endorsements, deductibles, and when collision or liability applies.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Windshield repair or replacement is usually covered under comprehensive coverage when damage comes from something other than a collision with another vehicle — rock chips, hail, vandalism, or falling objects. If you rear-end someone and your windshield cracks, [[collision coverage|/knowledge-base/auto/what-is-collision-car-insurance]] may apply instead. Many Texas insurers offer a separate full-glass or windshield endorsement with no deductible or a lower glass deductible than your standard comprehensive deductible. Liability-only policies do not cover your windshield replacement.',
        ],
      },
      {
        title: 'How coverage type affects glass claims',
        table: {
          headers: ['Cause of damage', 'Typical coverage', 'Deductible'],
          rows: [
            ['Rock chip on highway', 'Comprehensive', 'Comprehensive or $0 glass if endorsed'],
            ['Hail cracks windshield', 'Comprehensive', 'Often same as hail on body — see [[hail damage guide|/knowledge-base/auto/does-car-insurance-cover-hail-damage-texas]]'],
            ['Accident you cause', 'Collision', 'Collision deductible'],
            ['Another driver at fault', 'Their liability or your collision / UM options', 'Varies'],
            ['Liability-only policy', 'Not covered for your glass', 'N/A'],
          ],
        },
      },
      {
        title: 'Repair vs. full replacement',
        bullets: [
          'Small chips and cracks under a size threshold (often dollar-bill sized or less than a few inches) may be repaired free or at low cost — insurers prefer repair to avoid replacement.',
          'Damage in the driver\'s line of sight or long cracks usually require full windshield replacement.',
          'OEM vs. aftermarket glass — some policies specify aftermarket glass unless you pay the difference.',
          'ADAS cameras and sensors in the windshield may require recalibration — confirm whether your policy pays calibration fees.',
        ],
      },
      {
        title: 'Texas glass laws and claims tips',
        bullets: [
          'Texas does not require insurers to waive glass deductibles — waiver is a marketing feature on many policies, not state law.',
          'Multiple glass claims in a short period can affect renewal — ask your carrier how they treat glass-only losses.',
          'If a truck\'s unsecured load caused the damage, document plate and company — subrogation may recover your [[deductible|/knowledge-base/auto/how-do-car-insurance-deductibles-work]].',
        ],
      },
      {
        title: 'Should you file a claim for a small chip?',
        paragraphs: [
          'If you have a zero-deductible glass endorsement, repair is often worth filing. If the only coverage is comprehensive with a $500 or $1,000 deductible, a $200 repair may not exceed the deductible — you pay out of pocket. Compare long-term renewal effects with your agent. Pair windshield protection with [[full coverage|/knowledge-base/auto/full-coverage-vs-liability-only-car-insurance]] on vehicles you rely on daily.',
        ],
      },
    ],
    ctaHeading: 'Compare Texas auto insurance with GetSkimp',
    ctaBody: 'Answer a few questions and see car insurance options built for Texas drivers.',
  },

  'what-is-non-owner-car-insurance': {
    slug: 'what-is-non-owner-car-insurance',
    title: 'What is non-owner car insurance?',
    subtitle:
      'Non-owner policies provide liability when you drive borrowed or rented cars but do not own a vehicle. Useful for [[SR-22 requirements|/knowledge-base/auto/what-is-sr22-insurance-in-texas]] and frequent renters.',
    metaTitle: 'What Is Non-Owner Car Insurance? Texas | GetSkimp',
    metaDescription:
      'Non-owner car insurance in Texas — who needs it, what it covers, SR-22 filings, and how it differs from a standard auto policy when you borrow or rent cars.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'Non-owner car insurance is a liability-only policy for people who do not own a vehicle but still drive occasionally — borrowed cars from friends, rentals, or car-sharing. It pays for injuries and property damage you cause to others up to policy limits. It typically does not include comprehensive or collision for a vehicle you do not own (the owner\'s policy or rental coverage handles physical damage). In Texas, non-owner policies can satisfy [[SR-22 filing|/knowledge-base/auto/what-is-sr22-insurance-in-texas]] requirements when you need proof of financial responsibility without insuring a owned car.',
        ],
      },
      {
        title: 'Who should consider non-owner insurance',
        table: {
          headers: ['Profile', 'Why non-owner may fit'],
          rows: [
            ['License suspension / SR-22, no car', 'Meet DPS filing without insuring a garaged vehicle'],
            ['Frequent rental car user', 'Supplement liability beyond rental counter minimums'],
            ['Borrow cars from family regularly', 'Backup liability if owner\'s limits are low'],
            ['Between vehicles temporarily', 'Avoid coverage gap that raises future rates'],
            ['You own a car', 'Use a standard policy instead — non-owner is not for vehicle owners'],
          ],
        },
      },
      {
        title: 'What non-owner usually includes and excludes',
        bullets: [
          'Includes: bodily injury and property damage liability; optional [[UM/UIM|/knowledge-base/auto/uninsured-underinsured-motorist-coverage]] and [[MedPay|/knowledge-base/auto/what-is-medical-payments-coverage-medpay]] on some carriers.',
          'Excludes: comprehensive and collision on any specific car; vehicles in your household available for your regular use (you should be on that owner\'s policy).',
          'Excludes: business use, rideshare, and delivery — see [[rideshare coverage|/knowledge-base/auto/car-insurance-for-rideshare-drivers-uber-lyft]].',
          'Does not cover you when driving a car you own or that is registered to you — even if you call it "non-owner."',
        ],
      },
      {
        title: 'Non-owner vs. borrowing a friend\'s car',
        paragraphs: [
          'Texas policies generally follow the car first — the vehicle owner\'s insurance is primary when you borrow with permission. Non-owner acts as secondary liability in some situations or provides continuous proof of insurance for SR-22. If you live with the owner and drive regularly, you must usually be listed on their policy instead of relying on non-owner. Read [[does insurance follow the car or driver|/knowledge-base/auto/does-car-insurance-follow-the-car-or-driver]] for scenario details.',
        ],
      },
      {
        title: 'Shopping a non-owner policy in Texas',
        bullets: [
          'Select liability limits at least at [[30/60/25|/knowledge-base/auto/texas-minimum-liability-car-insurance]]; higher limits are recommended if you have assets.',
          'Confirm the carrier files SR-22 electronically with Texas DPS if required.',
          'Ask whether rental cars are covered as non-owned autos under the policy wording.',
          'Maintain continuous coverage to avoid [[penalties for lapses|/knowledge-base/auto/what-happens-if-you-drive-without-insurance-in-texas]].',
        ],
      },
    ],
    ctaHeading: 'Compare Texas auto insurance with GetSkimp',
    ctaBody: 'Answer a few questions and see car insurance options built for Texas drivers.',
  },

  'car-insurance-when-financing-or-leasing-a-vehicle': {
    slug: 'car-insurance-when-financing-or-leasing-a-vehicle',
    title: 'Car insurance when financing or leasing a vehicle',
    subtitle:
      'Lenders and lessors require more than Texas minimum liability — usually comprehensive, collision, and listed loss payee. Plan for [[gap insurance|/knowledge-base/auto/what-is-gap-insurance]] and correct deductibles before you drive off the lot.',
    metaTitle: 'Car Insurance for Financed & Leased Cars | GetSkimp',
    metaDescription:
      'Insurance requirements when financing or leasing a car in Texas — comp, collision, gap, loss payee, and what happens if your coverage lapses on a loan or lease.',
    sections: [
      {
        title: 'Quick answer',
        paragraphs: [
          'When you finance or lease a vehicle in Texas, the bank or leasing company requires you to carry physical damage coverage — comprehensive and collision — in addition to state-mandated liability. They will be listed as loss payee (loan) or additional insured / loss payee (lease) on your policy. Minimum liability alone does not satisfy the contract. If you cancel comp/collision or let the policy lapse, the lender may force-place expensive collateral protection insurance and charge you. Gap coverage is strongly recommended when you owe more than the car\'s value early in the loan.',
        ],
      },
      {
        title: 'Typical lender vs. lessor requirements',
        table: {
          headers: ['Requirement', 'Financed (loan)', 'Leased'],
          rows: [
            ['Liability insurance', 'Required — often higher than 30/60/25', 'Required — often 100/300 or lease-specified'],
            ['Comprehensive', 'Required', 'Required'],
            ['Collision', 'Required', 'Required'],
            ['Maximum deductible', 'Often $500 or $1,000 cap', 'Often $500 or $1,000 cap'],
            ['Gap / waiver', 'Recommended on loan', 'Often included in lease — verify contract'],
            ['Loss payee on policy', 'Lender listed', 'Lessor listed'],
          ],
        },
      },
      {
        title: 'Setting up insurance before delivery',
        numbered: [
          'Get a policy effective on or before the drive-away date — no gap in coverage.',
          'Provide the lender or dealer the insurer\'s loss payee clause and policy number for funding release.',
          'Choose [[deductibles|/knowledge-base/auto/how-do-car-insurance-deductibles-work]] within lender maximums.',
          'Add [[gap insurance|/knowledge-base/auto/what-is-gap-insurance]] if you put little down or stretch the loan term.',
          'Confirm [[full coverage|/knowledge-base/auto/full-coverage-vs-liability-only-car-insurance]] limits meet the finance contract, not just Texas legal minimums.',
        ],
      },
      {
        title: 'If your policy lapses',
        bullets: [
          'Force-placed CPI from the lender protects the bank\'s interest, not yours — it is costly and does not replace liability for you.',
          'Lease default and repossession risk increase with contract violations.',
          'Reinstating insurance and updating the loss payee promptly avoids CPI charges — see [[switching insurers|/knowledge-base/auto/how-to-switch-car-insurance-companies]] without a lapse.',
        ],
      },
      {
        title: 'Paying off the loan or buying out the lease',
        paragraphs: [
          'Once you own the title outright, comprehensive and collision become optional — you may drop physical damage if you can absorb a total loss. Keep strong [[liability and UM/UIM|/knowledge-base/auto/how-much-car-insurance-do-i-need-in-texas]]. On lease return, maintain coverage through the final day and document condition to avoid end-of-lease damage disputes. After payoff, review whether [[gap|/knowledge-base/auto/what-is-gap-insurance]] should be removed from your policy.',
        ],
      },
    ],
    ctaHeading: 'Compare Texas auto insurance with GetSkimp',
    ctaBody: 'Answer a few questions and see car insurance options built for Texas drivers.',
  },
};

export const AUTO_INSURANCE_CONFIG: InsuranceProductConfig = {
  lastModified: AUTO_LAST_MODIFIED,
  sidebarTitle: 'Auto Insurance',
  basePath: AUTO_BASE_PATH,
  qualifyInsuranceType: 'car',
  categories: AUTO_ARTICLE_CATEGORIES,
  contentBySlug: AUTO_ARTICLE_CONTENT,
};
